<template>
  <ModalComponent
    :dismissible="false"
    :no-header="true"
    :no-footer="true"
    :submit-button-text="$t('common.actions.accept')"
    @setForceToggle="setForceToggle"
  >
    <template #body>
      <!--
        Compact clickwrap card: no header band, no separate footer.
        See ModalComponent.noHeader/noFooter — these escape hatches let the
        body own the full chrome so a focused single-action prompt doesn't
        get the heavy padding/separators of the default header/footer.
      -->
      <div class="platform-agreement-card">
        <p class="agreement-intro mb-0">
          <i18n-t
            keypath="dashboard.buyer.platform_agreement_clickwrap_intro"
            scope="global"
          >
            <template #link>
              <a
                href="https://soldera.org/terms"
                target="_blank"
                rel="noopener noreferrer"
                class="agreement-link"
              >
                {{ $t("dashboard.buyer.platform_agreement_link_text") }}
              </a>
            </template>
          </i18n-t>
        </p>

        <hr class="agreement-divider" />

        <div class="agreement-controls">
          <div class="form-check mb-0">
            <input
              class="form-check-input"
              type="checkbox"
              v-model="agreedToTerms"
              id="termsCheckbox"
            />
            <label class="form-check-label" for="termsCheckbox">
              {{ $t("dashboard.buyer.agree_to_terms") }}
            </label>
          </div>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!agreedToTerms || isSubmitting"
            @click="handleAccept"
          >
            <span
              v-if="isSubmitting"
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            {{ $t("common.actions.accept") }}
          </button>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { Company } from "@/types";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  company: Company;
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const accountStore = useAccountStore();

const agreedToTerms = ref(true); // Pre-checked as per requirements
const isSubmitting = ref(false);
const forceToggle = ref<((value: boolean) => void) | null>(null);

const setForceToggle = (fn: (value: boolean) => void) => {
  forceToggle.value = fn;
  // Auto-open the modal when it's mounted
  setTimeout(() => {
    if (forceToggle.value) {
      forceToggle.value(true);
    }
  }, 100);
};

const handleAccept = async () => {
  if (!agreedToTerms.value || isSubmitting.value) {
    return;
  }

  isSubmitting.value = true;

  try {
    await accountStore.postAccountChange(
      `/api/company/${props.company.id}/confirm-registration`,
      { contract_version: "v5" }
    );

    globalSuccessHandler(
      t("common.success"),
      t("dashboard.buyer.terms_accepted")
    );

    forceToggle.value?.(false);
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped lang="scss">
.platform-agreement-card {
  padding: 1.5rem 1.5rem 1.25rem;
}

.agreement-intro {
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--phoenix-body-color);
}

.agreement-link {
  font-weight: 600;
  color: var(--phoenix-primary);
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: var(--phoenix-primary-emphasis, var(--phoenix-primary));
  }
}

.agreement-divider {
  margin: 1rem 0;
  border-color: var(--phoenix-border-color-subtle, rgba(0, 0, 0, 0.08));
}

.agreement-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;

  .form-check {
    // The checkbox group should stretch but not shove the button onto its own
    // line until the modal really runs out of horizontal room.
    flex: 1 1 auto;
    min-width: 0;
  }

  .btn-primary {
    flex: 0 0 auto;
  }
}
</style>
